import { computed, ref, shallowRef, onScopeDispose } from "vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { downloadFromUrl, downloadFromBlob } from "@/utils/downloadUtils";

const DEFAULT_POLLING_INTERVAL_MS = 3000;
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

export type AsyncTaskState =
  "idle" | "starting" | "processing" | "success" | "failed";

/**
 * @property progress - The meta a task last reported with `update_state(state="PROGRESS")`; sent
 *   only while `status` is "PROGRESS".
 */
export interface AsyncTaskStatusResponse<TResult, TProgress = unknown> {
  task_id: string;
  status: string;
  ready: boolean;
  result?: TResult;
  progress?: TProgress;
  error?: string;
}

export interface AsyncTaskStartResponse {
  task_id: string;
  status: "processing";
}

export interface UseAsyncTaskOptions {
  pollingIntervalMs?: number;
}

/** A task that settled without a result: it failed, ended without one, or its status could no longer be read. */
export class AsyncTaskError extends Error {
  override name = "AsyncTaskError";
}

/**
 * @property signal - Aborting it stops the polling and cancels the status request in flight
 * @property immediate - Poll at once rather than after the first interval, for a task that may already be done
 * @property onProgress - Called with the meta the task reports through `update_state(state="PROGRESS")`
 */
export interface PollAsyncTaskOptions<TProgress> {
  signal: AbortSignal;
  pollingIntervalMs?: number;
  immediate?: boolean;
  onProgress?: (progress: TProgress) => void;
}

/**
 * Poll a started task until it is ready and resolve with its result.
 *
 * For a caller that only needs the outcome; `useAsyncTask` builds its reactive state on top of this.
 * Polls go out one at a time, each `pollingIntervalMs` after the previous one answered, so a slow
 * status endpoint never has polls piling up on it.
 *
 * @throws {AsyncTaskError} When the task fails, ends without a result, or its status endpoint fails
 *   MAX_CONSECUTIVE_POLL_ERRORS times in a row
 * @throws The signal's reason, as soon as `signal` aborts
 */
export async function pollAsyncTask<TResult, TProgress = unknown>(
  taskId: string,
  {
    signal,
    pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS,
    immediate = false,
    onProgress,
  }: PollAsyncTaskOptions<TProgress>
): Promise<TResult> {
  let consecutivePollErrors = 0;
  if (!immediate) await sleep(pollingIntervalMs, signal);

  for (;;) {
    const status = await fetchTaskStatus<TResult, TProgress>(
      taskId,
      signal
    ).catch((pollError: unknown) => {
      // The aborted request rejects with axios' own cancellation error; callers expect the signal's reason.
      if (signal.aborted) throw signal.reason;
      consecutivePollErrors += 1;
      console.error("Error polling task status:", pollError);
      if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        throw new AsyncTaskError("Lost connection to task status endpoint");
      }
      return null;
    });
    // Don't trust the transport to have dropped an answer that raced the abort.
    signal.throwIfAborted();

    if (status) {
      consecutivePollErrors = 0;
      if (status.status === "PROGRESS" && status.progress !== undefined) {
        onProgress?.(status.progress);
      }
      if (status.ready) return resultOf(status);
    }
    await sleep(pollingIntervalMs, signal);
  }
}

export function useAsyncTask<TResult, TProgress = unknown>(
  options: UseAsyncTaskOptions = {}
) {
  const { pollingIntervalMs } = options;

  const state = ref<AsyncTaskState>("idle");
  const taskId = ref<string | null>(null);
  const result = ref<TResult | null>(null);
  // Latest progress the running task reported, or null until it reports any.
  const progress = shallowRef<TProgress | null>(null);
  const errorMessage = ref<string | null>(null);
  // The raw failure, when there is one to keep: an axios error carries the backend's error code,
  // which a message string cannot, and which is what support needs to find the server-side log.
  // shallowRef so the foreign error object is handed back as-is, not wrapped in a deep proxy.
  const error = shallowRef<unknown>(null);
  // Owns the current run. Each execute()/attach()/reset() aborts it and takes a fresh one: a
  // response that awaited across that belongs to a superseded run, and acting on it would
  // overwrite the current run's state, so every continuation below re-checks its signal after
  // awaiting. Aborting also stops that run's polling.
  let run = new AbortController();

  const isRunning = computed(
    () => state.value === "starting" || state.value === "processing"
  );

  /**
   * Start the task and poll it to completion.
   *
   * Resolves once the task has started, and settles exactly once, into `state`: a failed start is
   * reported the same way a failed task is ("failed", with `error` and `errorMessage` set) and is
   * never also rethrown. Doing both would leave every caller with two ways to hear about one
   * failure — and reporting from both of them relies on Vue flushing the `state` watcher before
   * the rejection is delivered, which it does not.
   *
   * Calling execute again supersedes the previous run entirely: its polling stops and any of its
   * responses still in flight are discarded, so a restarted task can't have its state overwritten
   * or its polling stopped by the run it replaced.
   */
  const execute = async <TArgs extends unknown[]>(
    startTaskFn: (...args: TArgs) => Promise<AsyncTaskStartResponse>,
    ...args: TArgs
  ) => {
    const signal = beginRun();
    state.value = "starting";

    let started: AsyncTaskStartResponse;
    try {
      started = await startTaskFn(...args);
    } catch (startError) {
      if (signal.aborted) return;
      error.value = startError;
      errorMessage.value = "Failed to start task";
      state.value = "failed";
      return;
    }
    if (signal.aborted) return;
    void followTask(started.task_id, signal, false);
  };

  /**
   * Resume polling a task started earlier (e.g. before a page reload). Supersedes any current run
   * exactly like `execute` does. Unlike a freshly started task, this one may already be finished,
   * so the first poll goes out at once rather than after the first interval.
   */
  const attach = (existingTaskId: string) => {
    void followTask(existingTaskId, beginRun(), true);
  };

  /**
   * Ask the backend to stop the running task. Polling continues until the task itself reports
   * ready, so the state settles into "failed" through the normal path. If the request fails, the
   * run settles into "failed" right away with the request error attached.
   */
  const cancel = async () => {
    if (!taskId.value || !isRunning.value) return;
    const { signal } = run;
    try {
      await goSolidApi.post(`/api/async-tasks/${taskId.value}/cancel`);
    } catch (cancelError) {
      // The task may have settled while the request was out; a failed cancel doesn't undo that.
      if (signal.aborted || !isRunning.value) return;
      supersedeRun();
      error.value = cancelError;
      errorMessage.value = "Failed to cancel task";
      state.value = "failed";
    }
  };

  const downloadResult = async () => {
    if (!taskId.value) return;
    const { signal } = run;

    try {
      const response = await goSolidApi.get(
        `/api/async-tasks/${taskId.value}/download`
      );
      if (signal.aborted) return;
      const { download_url, filename, requires_auth } = response.data;
      // The backend tells us which storage mode it's in via `requires_auth`. S3 presigned URLs are
      // self-authenticating, so a plain anchor click works. Local-storage downloads (dev/test) point
      // back at our own Bearer-protected API, which an anchor navigation can't reach (it drops the
      // Authorization header) — fetch those through the authenticated API client as a blob instead.
      if (requires_auth) {
        const fileResponse = await goSolidApi.get(download_url, {
          responseType: "blob",
        });
        if (signal.aborted) return;
        downloadFromBlob(fileResponse.data, filename);
      } else {
        downloadFromUrl(download_url, filename);
      }
    } catch {
      if (signal.aborted) return;
      state.value = "failed";
      errorMessage.value = "Failed to download result";
    }
  };

  const reset = () => {
    beginRun();
    state.value = "idle";
    taskId.value = null;
  };

  /** Poll `id` as the current run and settle `state` from its outcome, unless `signal` aborts first. */
  async function followTask(
    id: string,
    signal: AbortSignal,
    immediate: boolean
  ) {
    taskId.value = id;
    state.value = "processing";
    try {
      const value = await pollAsyncTask<TResult, TProgress>(id, {
        signal,
        pollingIntervalMs,
        immediate,
        onProgress: (reported) => (progress.value = reported),
      });
      // The poll can settle and its run be superseded before this continuation runs.
      if (signal.aborted) return;
      result.value = value;
      state.value = "success";
    } catch (pollError) {
      if (signal.aborted) return;
      if (!(pollError instanceof AsyncTaskError)) throw pollError;
      errorMessage.value = pollError.message;
      state.value = "failed";
    }
  }

  function beginRun() {
    supersedeRun();
    result.value = null;
    progress.value = null;
    errorMessage.value = null;
    error.value = null;
    return run.signal;
  }

  function supersedeRun() {
    run.abort();
    run = new AbortController();
  }

  onScopeDispose(() => run.abort());

  return {
    state,
    taskId,
    isRunning,
    downloadResult,
    result,
    progress,
    error,
    errorMessage,
    execute,
    attach,
    cancel,
    reset,
  };
}

async function fetchTaskStatus<TResult, TProgress>(
  taskId: string,
  signal: AbortSignal
) {
  const { data } = await goSolidApi.get<
    AsyncTaskStatusResponse<TResult, TProgress>
  >(`/api/async-tasks/${taskId}/status`, { signal });
  return data;
}

function resultOf<TResult>({
  error,
  result,
}: AsyncTaskStatusResponse<TResult, unknown>): TResult {
  if (error) throw new AsyncTaskError(error);
  if (result === undefined) {
    throw new AsyncTaskError("Task ended without a result");
  }
  return result;
}

/** Wait `ms`, or reject with the signal's reason as soon as `signal` aborts, clearing the timer. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    signal.throwIfAborted();
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(signal.reason);
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}
